import React from "react";
import { connect } from "react-redux";
import {
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import ptsLogo from "../assets/images/PTS-logo.png";
import IconAms from "../assets/images/icon-AMS.svg";
import splashScreen from "../assets/images/login-splash.jpg";
import {
  sendPassResetLink,
  setPasswordFieldShouldReset,
  validateResetPassHash,
  setSecondFactor,
  submitLoginForm,
} from "../reducers/UserReducer";
import { notify } from "../reducers/NotifierReducer";
import { LOGIN_2FA, LOGIN_VIEW, PASSWORD_RESET_VIEW } from "../utils/constants";
import { checkForUpdates } from "../reducers/AppInfoReducer";
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  card: {
    width: 480,
    maxWidth: "95%",
    background: "fff",
    "& img": {
      width: "100%",
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginBottom: "10px"
  },
  branding: {
    width: "100%",
    height: 200,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#52134E",
    "& img": {
      width: 150,
      height: "auto",
    },
  },
  branding2: {
    width: "100%",
    textAlign: "center",
    padding: "25px 0 5px",
    "& img": {
      height: 50,
      width: "auto",
    },
  },
  actions: {
    textAlign: "center",
    marginTop: 30,
    "& .customBtn": {
      background: "#52134E",
      "&:hover": {
        background: "#9D5FCC",
      },
    },
    '& button': {
      paddingLeft: '4em',
      paddingRight: '4em',
      textTransform: 'uppercase',
    },
    '& button.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  passReset: {
    textAlign: "right",
    "& span": {
      fontSize: 13,
      color: "#808080",
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  passResetDisabled: {
    pointerEvents: 'none',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  codeText: {
    color: '#0153a3',
    marginBottom: '2px',
    display: 'block',
  },
  version: {
    position: "absolute",
    bottom: "0.5em",
    left: "1em",
    lineHeight: 1,
  },
  formWrap: {
    '& .MuiInputBase-root': {
      color: '#333',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      boxShadow: 'none',
      textFillColor: 'inherit',
    },
  },
  textField: {
    width: '100%',
  },
}));

const PageLogin = (props) => {
  const classes = useStyles();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [view, setView] = React.useState(LOGIN_VIEW); // login, pass-reset
  const [showPassword, setShowPassword] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const { appInfo, secondFactor, setSecondFactor } = props;
  const [secondFactorToken, setSecondFactorToken] = React.useState('');
  const { appVersion } = appInfo;
  const [code, setCode] = React.useState(null);
  const history = useHistory();

  React.useEffect(() => {
    setSecondFactor(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (secondFactor === true) setView(LOGIN_2FA);
    if (secondFactor === false) setView(LOGIN_VIEW);
  }, [secondFactor]);

  React.useEffect(() => {
    props.checkForUpdates();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (props.passwordFieldShouldReset) {
      setPassword("");
    }
    props.setPasswordFieldShouldReset(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.passwordFieldShouldReset]);

  React.useEffect(() => {
    if (!props.otpRequired) {
      setOtp("");
    }
  }, [props.otpRequired]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleCodeChange = (event) => setCode(event.target.value);

  const clickCancel = () => {
    setCode(null);
    setUsername('');
    setView(LOGIN_VIEW);
    setSecondFactor(false);
  };

   const handleSecondFactorTokenChange = (event) => {
    setSecondFactorToken(event.target.value);
  };

  const submitForm = (event) => {
    event.preventDefault();
    props.submitLoginForm(username, password, otp);
  };

  const passResetSubmit = async (event) => {
    event.preventDefault();
    try {
      const valid = await validateResetPassHash(code, username);
      if (valid.status === 'expired') {
        props.notify('This code has expired. Please click resend code for a new code');
      } else if (valid.status === 'invalid') {
        props.notify('The code you entered is invalid. Please check code and try again');
      } else if (valid.status === 'valid') {
        history.push('/Reset-password', { user: username, code });
      }
    } catch (err) {
      console.log(err);
      props.notify("Error sending password reset code", "error");
    }
  };

   const secondFactorSubmit = async (event) => {
    event.preventDefault();
    props.submitLoginForm(username, password, secondFactorToken);
  };

  const passResetClick = async (isResendRequest = false) => {
    try {
      const response = await sendPassResetLink(username, isResendRequest);
      if (!response|| response === 'disabled') {
        setView('login');
        props.notify(
          response === 'disabled'
            ? 'User is disabled. Please contact system administrator.'
            : 'Username does not exist. Please enter a valid username.',
          response === 'disabled' ? 'error' : 'info'
        );
      } else {
        if (!isResendRequest) {
          setEmail(response);
          setView('pass-reset');
        } else {
          props.notify(
            'Your verification code has been resent. Please check your email.',
            'success'
          );
        }
      }
    } catch (err) {
      props.notify('Error sending password reset code', 'error');
    }
  }

  const renderLoginForm = () => (
    <form onSubmit={submitForm}>
      <div className="mb-3">
        <FormControl size="small" className="w-100">
          <TextField
            required
            variant="outlined"
            label="Username"
            fullWidth
            placeholder="Enter your username"
            type="text"
            value={username}
            onChange={handleUsernameChange}
            autoComplete="username"
            size="small"
            InputProps={{
              className: classes.input,
            }}
          />
        </FormControl>
      </div>
      <div className="mb-3">
        <FormControl size="small" className="w-100">
          <TextField
            inputRef={(input) =>
              props.passwordFieldShouldReset === true && input?.focus()
            }
            required
            variant="outlined"
            label="Password"
            fullWidth
            placeholder="Enter your password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={handlePasswordChange}
            autoComplete="current-password"
            size="small"
            disabled={props.otpRequired}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      </div>
      <div className={classes.passReset}>
        <span
          className={!username ? classes.passResetDisabled : ''} 
          onClick={() => passResetClick(false)}
        >
          Password reset
        </span>
      </div>
      <div className={classes.actions}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          size="large"
          className="customBtn"
        >
          Login
        </Button>
      </div>
    </form>
  )

  const renderPassReset = () => (
    <form onSubmit={passResetSubmit}>
      <div className={classes.formWrap}>
        <TextField
          required
          variant="outlined"
          label="Username"
          placeholder="Enter your username"
          type="text"
          value={username}
          onChange={handleUsernameChange}
          className={classes.textField}
          disabled
        />
      </div>

      {!code && (
        <span className={`${classes.codeText} mt-3 px-1 d`}>Check email for code sent to {email}</span>
      )}

      <div className={`${classes.formWrap} mt-2`}>
        <TextField
          type="number"
          required
          variant="outlined"
          label="Code"
          placeholder="Enter code here"
          value={code}
          onChange={handleCodeChange}
          className={classes.textField}
        />
      </div>

      <div className={classes.passReset}>
        <span onClick={() => passResetClick(true)}>Resend code</span>
      </div>

      <div className={classes.actions}>
        <div className="d-flex justify-content-between" style={{ paddingLeft: '32px', paddingRight: '32px'}}>
          <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={!code}
            className="customBtn">
            Continue
          </Button>
        </div>
      </div>
    </form>
  );

  const renderSecondFactor = () => {
    return (
      <form onSubmit={secondFactorSubmit}>
        <div className={classes.formWrap}>
          <TextField
            required
            variant="outlined"
            label="Token"
            fullWidth
            placeholder="Enter token"
            type="text"
            size="small"
            value={secondFactorToken}
            onChange={handleSecondFactorTokenChange}
            InputProps={{
              className: classes.input,
            }}
          />
        </div>
        <div className={classes.actions}>
          <div className="d-flex justify-content-between" style={{ paddingLeft: '32px', paddingRight: '32px'}}>
            <Button type="button" variant="outlined" size="large" onClick={clickCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="large"
              className="customBtn">
              Login
            </Button>
          </div>
        </div>
      </form>
    );
  };
  

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        <div className={classes.branding}>
          <img src={IconAms} alt="Asset Management Icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        <CardContent className={classes.content}>
          {view === LOGIN_VIEW && renderLoginForm()}
          {view === LOGIN_2FA && renderSecondFactor()}
          {view === PASSWORD_RESET_VIEW && renderPassReset()}
          <div className={classes.version}>{appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  passwordFieldShouldReset: state.user.passwordFieldShouldReset,
  secondFactor: state.user.secondFactor,
});

export default connect(mapStateToProps, {
  submitLoginForm,
  checkForUpdates,
  notify,
  setPasswordFieldShouldReset,
  setSecondFactor,
})(PageLogin);
